import styled from "@emotion/styled";
import React, { ReactNode, useEffect, useRef } from "react";
import { color_shades_dark, color_shades_darkest, color_variants_blue_2, color_variants_gold_light, color_variants_green_2, color_variants_red_2, color_variants_shadow_10 } from "../../constants/colors";
import Button from "./Button";

const closeIcon = require('../../content/images/close_icon.svg');
const redIcon = require('../../content/images/nlp-icons/training-indicator-red.svg');
const greenIcon = require('../../content/images/nlp-icons/training-indicator-green.svg');


const loadingGif = require('../../content/images/nlp-icons/brainimation.gif');

interface StatusIndicatorProps {
    state: "success" | "loading" | "fail" | "warning"
    successMessage: string
    loadingMessage: string
    failedMessage: string
    warningMessage?: string
    isClosable?: boolean
    onClose?: () => void
    onAcceptFailure?: () => void
    /** Replaces the default training icons, e.g. for a sync instead of a training. */
    icons?: { loading?: string, success?: string, fail?: string, warning?: string }
    /** Spins the loading icon; use with a static loading icon. */
    spinLoadingIcon?: boolean
    /** Extra details shown under the message. */
    children?: ReactNode
    /** Shows a close icon in the top right instead of the "Got It" button; it calls onAcceptFailure for fail/warning and onClose otherwise. */
    dismissWithClose?: boolean
}

export default function StatusIndicator(props: StatusIndicatorProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);

    const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
            if (props.isClosable) {
                props.onClose();
            }
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        //clean up
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [props]);

    const renderMessage = (message: string) => props.children ?
        <IndicatorBody>
            <IndicatorText className="with-details">{message}</IndicatorText>
            {props.children}
        </IndicatorBody>
        :
        <IndicatorText>{message}</IndicatorText>;

    const renderCornerClose = (onClick: () => void) => <CornerCloseButton type="button" aria-label="Close" onClick={onClick}>
        <img src={closeIcon} alt="" />
    </CornerCloseButton>;

    if (props.state === "success") {
        return <IndicatorWrapper ref={wrapperRef}>
            <GreenIndicator>
                <TrainingIcon src={props.icons?.success ?? greenIcon} />
                {renderMessage(props.successMessage)}
                {props.dismissWithClose ? renderCornerClose(props.onClose)
                    : props.isClosable ? <CloseIcon src={closeIcon} onClick={props.onClose} /> : <></>}
            </GreenIndicator>
        </IndicatorWrapper>
    }

    if (props.state === "loading") {
        return <IndicatorWrapper ref={wrapperRef}>
            <BlueIndicator>
                {props.icons?.loading ?
                    <TrainingIcon src={props.icons.loading} className={props.spinLoadingIcon ? "spin" : ""} />
                    :
                    <TrainingGif src={loadingGif} />
                }
                {renderMessage(props.loadingMessage)}
                {props.isClosable ? <CloseIcon src={closeIcon} onClick={props.onClose} /> : <></>}
            </BlueIndicator>
        </IndicatorWrapper>
    }

    if (props.state === "fail" || props.state === "warning") {
        const isWarning = props.state === "warning";
        const Indicator = isWarning ? GoldIndicator : RedIndicator;
        return <IndicatorWrapper ref={wrapperRef}>
            <Indicator>
                <TrainingIcon src={(isWarning ? props.icons?.warning : props.icons?.fail) ?? redIcon} />
                {renderMessage(isWarning ? props.warningMessage : props.failedMessage)}

                {props.dismissWithClose ? renderCornerClose(props.onAcceptFailure) :
                    <AcceptButtonWrapper className={props.children ? "with-details" : ""}>
                        <Button
                            themes={["primary-small"]}
                            text={"Got It"}
                            type="button"
                            onClick={props.onAcceptFailure}
                        />
                    </AcceptButtonWrapper>
                }
                {!props.dismissWithClose && props.isClosable ? <CloseIcon src={closeIcon} onClick={props.onClose} /> : <></>}
            </Indicator>
        </IndicatorWrapper>
    }

    return <></>
}


const IndicatorText = styled.p`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: ${color_shades_darkest};
    margin: auto auto auto 50px;

    &.with-details {
        margin: 0;
    }
`;

const IndicatorBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 0;
    margin: auto 48px auto 32px;
    padding: 20px 0;
`;

const TrainingGif = styled.img`
    width: 96px;
    height: 92px;
    margin: auto 0px auto 16px;
`;

const TrainingIcon = styled.img`
    width: 88px;
    height: 80px;
    margin: auto 0px auto 16px;
    flex-shrink: 0;

    &.spin {
        animation: statusIndicatorSpin 1.6s linear infinite;
    }

    @keyframes statusIndicatorSpin {
        to { transform: rotate(360deg); }
    }
`;

const CloseIcon = styled.img`
    margin: auto 32px auto auto;
    width: 12px;
    height: 12px;
`;

const CornerCloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 1;
    }

    img {
        width: 12px;
        height: 12px;
    }
`;

const IndicatorWrapper = styled.div`
    position: fixed;
    bottom: 32px;
    right: 32px;
    width: 528px;
    max-width: calc(100vw - 64px);
    min-height: 104px;
    border-radius: 8px;
    border: 1px solid ${color_shades_dark};
    box-shadow: 0px 4px 20px ${color_variants_shadow_10};
    z-index: 1100;
    background: white;
    box-sizing: border-box;
    cursor: pointer;
`;

const Indicator = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    min-height: 102px;
    border-radius: 8px;
    width: 100%;
`;

const GreenIndicator = styled(Indicator)`
    background: ${color_variants_green_2};
`;

const RedIndicator = styled(Indicator)`
    background: ${color_variants_red_2};
`;

const GoldIndicator = styled(Indicator)`
    background: ${color_variants_gold_light};
`;

const BlueIndicator = styled(Indicator)`
    background: ${color_variants_blue_2};
`;

const AcceptButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;

    &.with-details {
        width: auto;
        margin: 0 16px;
    }
`
